import React, { useState, useEffect, useRef } from 'react';
import { Search, Download, Award, FileText, CheckCircle, XCircle, TrendingUp, Printer } from 'lucide-react';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import { formatDate } from '../../utils/dateTimeUtils';

const Results = () => {
  const [filters, setFilters] = useState({ 
    course_id: '', 
    semester_id: '', 
    exam_id: '' 
  });
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [allSemesters, setAllSemesters] = useState([]);
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    passed: 0,
    failed: 0,
    passRate: 0
  });
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedExamDetails, setSelectedExamDetails] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [collegeInfo, setCollegeInfo] = useState(null);
  const printRef = useRef();

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCourses();
    fetchSemesters();
    fetchExams();
    fetchSubjects();
    loadCollegeInfo();
  }, []);

  const loadCollegeInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tenants/my-college/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
          logo_url: response.data.logo || response.data.logo_url
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/semesters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllSemesters(response.data);
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/examination/exams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExams(response.data);
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const handleCourseChange = (courseId) => {
    setFilters({ course_id: courseId, semester_id: '', exam_id: '' });
    
    if (courseId) {
      const filteredSemesters = allSemesters.filter(sem => sem.course_id === courseId);
      setSemesters(filteredSemesters);
    } else {
      setSemesters(allSemesters);
    }
    setResults([]);
  };

  const generateResults = async () => {
    if (!filters.course_id || !filters.semester_id || !filters.exam_id) {
      alert('Please select Course, Semester and Exam');
      return;
    }

    setLoading(true);
    try {
      // Get exam details
      const examResponse = await axios.get(
        `${API_URL}/api/examination/exams/${filters.exam_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const examData = examResponse.data;

      // Get students
      const studentsResponse = await axios.get(`${API_URL}/api/students/dropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const studentsData = Array.isArray(studentsResponse.data) ? studentsResponse.data : (studentsResponse.data?.data || []);
      const filteredStudents = studentsData.filter(
        s => s.course_id === filters.course_id && s.semester_id === filters.semester_id
      );

      // Get subjects for this semester
      const semesterSubjects = subjects.filter(
        sub => sub.course_id === filters.course_id && sub.semester_id === filters.semester_id
      );

      // Generate results for each student
      const studentResults = [];
      
      for (const student of filteredStudents) {
        const subjectMarks = [];
        let totalObtained = 0;
        let totalMaxMarks = 0;
        let hasAllMarks = true;
        let failedSubjects = [];

        for (const subject of semesterSubjects) {
          // Get marks for this subject
          const marksResponse = await axios.get(
            `${API_URL}/api/examination/marks`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                exam_id: filters.exam_id,
                subject_id: subject.id,
                student_id: student.id
              }
            }
          );

          if (marksResponse.data && marksResponse.data.length > 0) {
            const mark = marksResponse.data[0];
            subjectMarks.push({
              subject: subject,
              marks: mark.marks_obtained,
              total: mark.total_marks,
              grade: mark.grade
            });
            totalObtained += mark.marks_obtained;
            totalMaxMarks += mark.total_marks;

            // Check if failed in this subject
            const passingMarks = examData.passing_marks || 40;
            const percentage = (mark.marks_obtained / mark.total_marks) * 100;
            if (percentage < passingMarks) {
              failedSubjects.push(subject.name);
            }
          } else {
            hasAllMarks = false;
          }
        }

        if (hasAllMarks && subjectMarks.length > 0) {
          const percentage = totalMaxMarks > 0 ? ((totalObtained / totalMaxMarks) * 100).toFixed(2) : 0;
          const overallGrade = calculateOverallGrade(percentage);
          const result = failedSubjects.length === 0 ? 'PASS' : 'FAIL';

          studentResults.push({
            student: student,
            subjectMarks: subjectMarks,
            totalObtained: totalObtained,
            totalMaxMarks: totalMaxMarks,
            percentage: percentage,
            grade: overallGrade,
            result: result,
            failedSubjects: failedSubjects
          });
        }
      }

      // Sort by percentage
      studentResults.sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage));

      // Add rank
      studentResults.forEach((result, index) => {
        result.rank = index + 1;
      });

      // Calculate stats
      const passed = studentResults.filter(r => r.result === 'PASS').length;
      const failed = studentResults.filter(r => r.result === 'FAIL').length;
      const total = studentResults.length;
      const passRate = total > 0 ? ((passed / total) * 100).toFixed(1) : 0;

      setStats({
        total: total,
        passed: passed,
        failed: failed,
        passRate: passRate
      });

      setResults(studentResults);

      // Store exam details for marksheet
      const course = courses.find(c => c.id === filters.course_id);
      const semester = semesters.find(s => s.id === filters.semester_id);
      setSelectedExamDetails({
        exam: examData,
        course: course,
        semester: semester,
        subjects: semesterSubjects
      });

    } catch (error) {
      console.error('Error generating results:', error);
      alert('Failed to generate results: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateOverallGrade = (percentage) => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C';
    if (percentage >= 40) return 'D';
    return 'F';
  };

  const exportResults = () => {
    alert('Export functionality will be implemented');
  };

  const printStudentMarksheet = (studentResult) => {
    setSelectedStudent(studentResult);
    setShowPrintModal(true);
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Marksheet_${selectedStudent?.student.admission_number || 'Student'}`,
    onAfterPrint: () => {
      setShowPrintModal(false);
    }
  });

  const filteredExams = exams.filter(exam => 
    (!filters.course_id || exam.course_id === filters.course_id) &&
    (!filters.semester_id || exam.semester_id === filters.semester_id)
  );

  // Filter results by search term
  const filteredResults = results.filter(result => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      result.student.full_name?.toLowerCase().includes(searchLower) ||
      result.student.admission_number?.toLowerCase().includes(searchLower) ||
      result.student.roll_number?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exam Results</h1>
          <p className="text-sm text-gray-500 mt-1">View student exam results based on marks entered</p>
        </div>
        {results.length > 0 && (
          <button 
            onClick={exportResults}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            <Download className="h-4 w-4" />
            Export Results
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Students</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <Award className="h-12 w-12 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pass</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{stats.passed}</p>
            </div>
            <CheckCircle className="h-12 w-12 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Fail</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{stats.failed}</p>
            </div>
            <XCircle className="h-12 w-12 text-red-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pass Rate</p>
              <p className="text-3xl font-bold text-purple-600 mt-1">{stats.passRate}%</p>
            </div>
            <TrendingUp className="h-12 w-12 text-purple-500" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.course_id} 
              onChange={(e) => handleCourseChange(e.target.value)}
            >
              <option value="">Select Course</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Semester *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.semester_id} 
              onChange={(e) => setFilters({ ...filters, semester_id: e.target.value })}
              disabled={!filters.course_id}
            >
              <option value="">{filters.course_id ? 'Select Semester' : 'Select course first'}</option>
              {semesters.map(sem => (
                <option key={sem.id} value={sem.id}>{sem.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Exam *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.exam_id} 
              onChange={(e) => setFilters({ ...filters, exam_id: e.target.value })}
              disabled={!filters.course_id || !filters.semester_id}
            >
              <option value="">{filters.semester_id ? 'Select Exam' : 'Select semester first'}</option>
              {filteredExams.map(exam => (
                <option key={exam.id} value={exam.id}>{exam.name} - {exam.exam_type}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button 
              onClick={generateResults}
              disabled={!filters.course_id || !filters.semester_id || !filters.exam_id || loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Loading...' : 'Generate Results'}
            </button>
          </div>
        </div>
      </div>

      {results.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search by name or admission number..." 
              className="pl-10 pr-4 py-2 w-full border rounded-lg" 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
            />
          </div>
        </div>
      )}

      {results.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total Marks</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Percentage</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Grade</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Result</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredResults.map((result, index) => (
                  <tr key={result.student.id} className={result.result === 'FAIL' ? 'bg-red-50' : ''}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {result.rank}
                      {result.rank === 1 && <Award className="inline h-4 w-4 text-yellow-500 ml-1" />}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {result.student.admission_number || result.student.roll_number || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{result.student.full_name}</td>
                    <td className="px-4 py-3 text-center text-sm font-semibold">
                      {result.totalObtained}/{result.totalMaxMarks}
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-semibold">
                      {result.percentage}%
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        result.grade === 'F' ? 'bg-red-100 text-red-800' :
                        result.grade === 'A+' || result.grade === 'A' ? 'bg-green-100 text-green-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {result.grade}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {result.result === 'PASS' ? (
                        <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          PASS
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          <XCircle className="h-3 w-3 mr-1" />
                          FAIL
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => printStudentMarksheet(result)}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                      >
                        <Printer className="h-3 w-3" />
                        Print Marksheet
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No results available. Select Course, Semester and Exam to generate results.</p>
        </div>
      )}

      {/* Print Modal */}
      {showPrintModal && selectedStudent && selectedExamDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-lg font-semibold">Marksheet Preview</h3>
              <div className="flex gap-2">
                <button
                  onClick={handlePrint}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
                >
                  <Printer className="h-4 w-4" />
                  Print
                </button>
                <button
                  onClick={() => setShowPrintModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
            <div ref={printRef} className="p-8 bg-white">
            {/* Tenant Header */}
            {(() => {
              const tenantInfo = collegeInfo || {};
              return (
                <div className="text-center mb-4 border-b pb-3">
                  {tenantInfo.logo_url && (
                    <img src={tenantInfo.logo_url} alt="Logo" className="h-14 mx-auto mb-2" />
                  )}
                  <h1 className="text-2xl font-bold text-gray-900">{tenantInfo.college_name || tenantInfo.name || 'Institution Name'}</h1>
                  <p className="text-sm text-gray-600">{tenantInfo.address || ''}</p>
                  <p className="text-xs text-gray-500">
                    {tenantInfo.phone && `Phone: ${tenantInfo.phone}`}
                    {tenantInfo.phone && tenantInfo.email && ' | '}
                    {tenantInfo.email && `Email: ${tenantInfo.email}`}
                  </p>
                  {tenantInfo.website && (
                    <p className="text-xs text-blue-600">{tenantInfo.website}</p>
                  )}
                </div>
              );
            })()}
            
            {/* Marksheet Title */}
            <div className="text-center mb-6 border-b-2 border-gray-800 pb-4">
              <h2 className="text-xl font-semibold text-gray-800 border-2 border-gray-800 inline-block px-6 py-2">STUDENT MARKSHEET</h2>
            </div>

            {/* Exam Details */}
            <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
              <div>
                <p><strong>Course:</strong> {selectedExamDetails.course?.name}</p>
                <p><strong>Semester:</strong> {selectedExamDetails.semester?.name}</p>
              </div>
              <div>
                <p><strong>Exam:</strong> {selectedExamDetails.exam?.name}</p>
                <p><strong>Exam Type:</strong> {selectedExamDetails.exam?.exam_type}</p>
                <p><strong>Session:</strong> {new Date(selectedExamDetails.exam?.start_date).getFullYear()}</p>
              </div>
            </div>

            {/* Student Details */}
            <div className="bg-gray-100 p-4 mb-6 rounded">
              <h3 className="font-semibold text-lg mb-2">Student Information</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <p><strong>Name:</strong> {selectedStudent.student.full_name}</p>
                <p><strong>Admission No:</strong> {selectedStudent.student.admission_number || selectedStudent.student.roll_number || 'N/A'}</p>
                <p><strong>Email:</strong> {selectedStudent.student.email || 'N/A'}</p>
                <p><strong>Mobile:</strong> {selectedStudent.student.mobile_number || 'N/A'}</p>
              </div>
            </div>

            {/* Marks Table */}
            <table className="w-full border-collapse border border-gray-800 mb-6">
              <thead>
                <tr className="bg-gray-200">
                  <th className="border border-gray-800 px-4 py-2 text-left">S.No</th>
                  <th className="border border-gray-800 px-4 py-2 text-left">Subject Code</th>
                  <th className="border border-gray-800 px-4 py-2 text-left">Subject Name</th>
                  <th className="border border-gray-800 px-4 py-2 text-center">Marks Obtained</th>
                  <th className="border border-gray-800 px-4 py-2 text-center">Total Marks</th>
                  <th className="border border-gray-800 px-4 py-2 text-center">Grade</th>
                </tr>
              </thead>
              <tbody>
                {selectedStudent.subjectMarks.map((subjectMark, index) => (
                  <tr key={index}>
                    <td className="border border-gray-800 px-4 py-2">{index + 1}</td>
                    <td className="border border-gray-800 px-4 py-2">{subjectMark.subject.code}</td>
                    <td className="border border-gray-800 px-4 py-2">{subjectMark.subject.name}</td>
                    <td className="border border-gray-800 px-4 py-2 text-center font-semibold">{subjectMark.marks}</td>
                    <td className="border border-gray-800 px-4 py-2 text-center">{subjectMark.total}</td>
                    <td className="border border-gray-800 px-4 py-2 text-center font-semibold">{subjectMark.grade}</td>
                  </tr>
                ))}
                <tr className="bg-gray-100 font-bold">
                  <td colSpan="3" className="border border-gray-800 px-4 py-2 text-right">TOTAL</td>
                  <td className="border border-gray-800 px-4 py-2 text-center">{selectedStudent.totalObtained}</td>
                  <td className="border border-gray-800 px-4 py-2 text-center">{selectedStudent.totalMaxMarks}</td>
                  <td className="border border-gray-800 px-4 py-2 text-center">{selectedStudent.grade}</td>
                </tr>
              </tbody>
            </table>

            {/* Result Summary */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="border border-gray-800 p-4">
                <p className="text-sm"><strong>Percentage:</strong> {selectedStudent.percentage}%</p>
                <p className="text-sm"><strong>Overall Grade:</strong> {selectedStudent.grade}</p>
              </div>
              <div className="border border-gray-800 p-4">
                <p className="text-sm"><strong>Result:</strong> <span className={selectedStudent.result === 'PASS' ? 'text-green-600' : 'text-red-600'}>{selectedStudent.result}</span></p>
                <p className="text-sm"><strong>Rank:</strong> {selectedStudent.rank}</p>
              </div>
            </div>

            {selectedStudent.failedSubjects.length > 0 && (
              <div className="bg-red-50 border border-red-300 p-3 mb-6 rounded">
                <p className="text-sm text-red-800">
                  <strong>Failed Subjects:</strong> {selectedStudent.failedSubjects.join(', ')}
                </p>
              </div>
            )}

            {/* Footer */}
            <div className="mt-8 pt-4 border-t border-gray-400">
              <div className="grid grid-cols-3 gap-4 text-center text-sm">
                <div>
                  <p className="mt-8 border-t border-gray-800 pt-2">Date of Issue</p>
                  <p>{formatDate()}</p>
                </div>
                <div>
                  <p className="mt-8 border-t border-gray-800 pt-2">Examiner Signature</p>
                </div>
                <div>
                  <p className="mt-8 border-t border-gray-800 pt-2">Principal Signature</p>
                </div>
              </div>
            </div>

            <div className="mt-4 text-center text-xs text-gray-500">
              <p>This is a computer-generated marksheet</p>
            </div>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default Results;